/**
 * ---------COMMON ENUMS & TYPES------------
 */
export enum CAF_ANALYSIS_TYPE_MAP {
  overall = 0,
  complexity = 1, // repetition
  accuracy = 2, // grammar
  fluency = 3, // mlr, speech_rate, pause
  pronunciation = 4,
  all = 5, // CAF Trend Graph - complexity + accuracy + fluency
}

/**
 * 하이라이트 태그 넘버별 태그명 (서버 응답 highlight_tag 또는 tag)
 */
export enum HIGHLIGHT_TAG_MAP {
  /**  Grammar Error (0~12)*/
  'det', //0
  'part', //1
  'prep', //2
  'verb_form', //3
  'noun_num', //4
  'wo', //5
  'morph', //6
  'sva', //7
  'adj', //8
  'adj_form', //9
  'adv', //10
  'noun_infl', //11
  'verb_tense', //12
  /**  Other Errors (13~17)*/
  'repetition', //13
  'ros', //14
  'speech_rate', //15
  'filler_word', //16
  'sentence_structure', // 17
  'pronunciation',
}
export enum CAF_A_TAG_MAP {
  'det',
  'part',
  'prep',
  'verb_form',
  'noun_num',
  'word',
  'morph',
  'sva',
  'adj',
  'adj_form',
  'adv',
  'noun_infl',
  'verb_tense',
}
export enum CAF_C_TAG_MAP {
  'word',
  'sentence',
}
export enum CAF_F_TAG_MAP {
  'speed',
  'pause',
}
export enum CAF_P_TAG_MAP {
  'articulation',
  'prosody',
}

export enum HIGHLIGHT_METRIC {
  REPETITION = 'repetition',
  GRAMMAR = 'grammar_error',
  MLR = 'mlr',
  SPEECH_RATE = 'speech_rate',
  RUNON = 'runon',
  FILLER_WORD = 'filler_word',
  SENTENCE_STRUCTURE = 'sentence_structure',
  PRONUNCIATION = 'pronunciation',
}

export enum METRIC_TAG {
  'vocab_range',
  'sentence_structure',
  'mtld',
  'epc',
  'speech_rate', //말하기 속도 (wpm)
  'mlr', //끊김없이 말한 단어수(words)
  'filler_word',
  'pause', //공백 비율(%)
  'ngram',
  'articulation',
  'prosody',
}

export enum AVERAGE_TYPES {
  'vocab',
  'sentence',
  'mtld',
  'epc',
  'speech_rate',
  'mlr',
  'filler_word',
  'pause',
  'ngram',
  'articulation',
  'prosody',
}
export type CAFAverageTypes = keyof typeof AVERAGE_TYPES
export enum CAF_ANALYSIS_STATUS {
  STUDENT_NOSHOW = 'no_show',
  RECORD_NOT_ALLOWED = 'no_record',
  ANALYSIS_NOT_ALLOWED = 'no_analysis',
  FREE_MATERIAL = 'free_material',
  IN_PROGRESS = 'in_progress',
  READY = 'ready',
}

// metric_tag
export enum PRONUNCIATION_METRIC {
  pronunciation = 'Pronunciation',
  prosody = 'Prosody',
}
export enum COMPLEXITY_METRIC {
  advanced = 'Advanced',
  high = 'High Intermediate',
  inter = 'Intermediate',
  low = 'Low Intermediate',
  beginner = 'Beginner',
}
export enum COMPLEXITY_METRIC_RATIO {
  'Advanced' = 'advanced',
  'High Intermediate' = 'high',
  'Intermediate' = 'inter',
  'Low Intermediate' = 'low',
  'Beginner' = 'beginner',
}

export enum COMPLEXITY_COMPOUND_METRIC {
  cc = 'Compound-complex',
  complex = 'Complex',
  compound = 'Compound',
  simple = 'Simple',
  other = 'Not a Sentence',
}
export enum COMPLEXITY_COMPOUND_METRIC_RATIO {
  'Compound-complex' = 'cc',
  'Complex' = 'complex',
  'Compound' = 'compound',
  'Simple' = 'simple',
  'Not a Sentence' = 'other',
}

/** 18가지 품사 */
export enum COMPLEXITY_PARTS_LIST {
  ADJ = 'adjective',
  ADV = 'adverb',
  CONJ = 'conjunction',
  DET = 'determiner',
  NOUN = 'noun',
  NUM = 'numeral',
  PART = 'particle',
  PRON = 'pronoun',
  VERB = 'verb',
  AUX = 'auxiliary',
  PREP = 'preposition',
  ADJ_FORM = 'adjective form',
  VERB_FORM = 'verb form',
  WO = 'word order',
  MORPH = 'morphology',
  SVA = 'subject-verb agreement',
  NOUN_INFL = 'noun inflection',
  VERB_TENSE = 'verb tense',
}

/**
 * ------- API Responses TYPE --------
 * route : webview_apis.caf 하위 APIs
 */

export interface CAFOverviewResponse {
  components: OverviewComponentItem[]
  popup_type: number
  show_popup
}
export interface HighlightListResponse {
  highlights: HighlightItem[]
}
export interface HighlightOverviewResponse extends HighlightListResponse {
  check_caf: boolean
}
export interface AccuracyMetricsDetailResponse {
  details: AccuracyMetricsDetailItem[]
}
export interface AverageResponse {
  top_25_ratio: number
  native_ratio: number
}
export interface MetricAverageResponse<T extends CAFAverageTypes> {
  native_ratio: { [key in MetricAverage<T>]: number | string }
  top_25_ratio: { [key in MetricAverage<T>]: number | string }
}
export type MetricAverage<T extends CAFAverageTypes> = T extends 'sentence'
  ? keyof typeof COMPLEXITY_COMPOUND_METRIC
  : T extends 'vocab'
  ? keyof typeof COMPLEXITY_METRIC
  : 'value'
export interface HighlightDetailResponse {
  highlight_id: number
  highlight_tag: number
  is_pinned: boolean
  active: boolean
  component: CAF_ANALYSIS_TYPE_MAP
  details: MetricsDetailItem[]
  page: PageItem
  count?: number
  interval_count?: number // speech_rate, filler_word에서는 count 대신 interval_count
  graph?: { data: SPEECH_RATE_GRAPH_DETAIL[] & FILLER_WORD_GRAPH_DETAIL[] }
}

interface ComponentItem {
  component_tag: number
  name: string
  level_rough: number
  level_exact?: number
}
interface OverviewComponentItem extends ComponentItem {
  id: number
}
export interface AnalysisCompDetailResponse extends ComponentItem {
  percentile: number
  epc?: number
}

/**
 * MetricsDetailItems
 */

export interface MetricsDetailItem
  extends SpeechRateDetailItem,
    GrammarDetailItem,
    RepetitionDetailItem,
    RunOnDetailItem,
    FillerWordDetailItem,
    PronunciationDetailItem {}

interface MetricDetailItemCommon {
  id: number
  audio_url: string
  sentence?: string
  my_sentence?: string
}
interface SpeechRateDetailItem extends MetricDetailItemCommon {
  start_time: string
  end_time: string
  duration_sec: number
}
interface FillerWordDetailItem extends MetricDetailItemCommon {
  filler_duration: number
}
interface RepetitionDetailItem extends MetricDetailItemCommon {
  repeated_word: string
  repeated_count: number
  correction: AIParaphraseCorrection
  corrections_count: number
}
interface PronunciationDetailItem extends MetricDetailItemCommon {
  word_error_index: {
    index: number
    right: string
    wrong: string
  }[]
  main_sentence: {
    audio_url: string
    is_main: boolean
    sentence: string
    sentence_error_index: { index: number; length: number }[]
  }
  sentences: { sentence: string; is_main: boolean }[]
  syllables: string[]
  word: string
}
interface RunOnDetailItem extends MetricDetailItemCommon {
  correction: AIParaphraseCorrection
  corrections_count: number
}
interface GrammarDetailItem {
  category: number
  components: GrammarCompItem[]
}
interface GrammarErrorDetailItem extends MetricDetailItemCommon {
  text: string
  errors: any
}
export interface GrammarCompItem extends GrammarErrorDetailItem {
  correct_sentence: string
  correction: AIParaphraseCorrection | null
}
export interface ReflectionItem {
  id: number
  text: string
  date: string
}

export interface HighlightItem {
  id: number
  component: string
  highlight_tag: number
  metric: HIGHLIGHT_METRIC
  active: boolean
  count: number
  details?: HighlightDetailItem & [] // ((pinned === true || recommended === true) && show_details === true) only
  is_pinned?: boolean // (pinned === false && recommended === false) only
  zscore?: number // (pinned === false && recommended === false) only
}

interface HighlightDetailItem
  extends MetricDetailItemCommon, // runon
    FluencyHighlightDetailItem, // speech_rate & filler
    GrammarCompItem {} // accuracy

interface FluencyHighlightDetailItem {
  detail_count: number
  total_count: number
  graph_data: SPEECH_RATE_GRAPH_DETAIL[] & FILLER_WORD_GRAPH_DETAIL[]
}

interface SPEECH_RATE_GRAPH_DETAIL {
  sentence: number
  wpm: number
}

interface FILLER_WORD_GRAPH_DETAIL {
  turn: number
  filler_ratio: number
}

/**
 * webview_apis.caf.analysisComponents
 */

export interface AnalysisComponentDetailMetrics {
  metric_id: number
  metric_tag: number
  metric_type: string
  details?: AccuracyDetailItem[] & ComplexityDetailItem[]
  value?: string // fluency | complexity
  graph_data?: AnalysisComponentDetailListGraphData | { data: ComplexityGraphDataDatumItem[] }
}
export interface AnalysisComponentDetailList {
  metrics: AnalysisComponentDetailMetrics[]
}

interface AnalysisComponentDetailListGraphData {
  id?: string
  data: FluencyGraphDatumItem[]
  color?: string
}
interface FluencyGraphDatumItem {
  x?: number
  y?: number
  sentence?: number // speech_rate
  wpm?: number // speech_rate
  turn?: number // mlr, pause, filler_word, ngram
  mlr?: number
  silent_rate?: number
  filler_ratio?: number
  ngram_ratio?: number
}
export interface AccuracyDetailItem {
  error_tag: number
  error_type: string
  count: number
  active: boolean
}

export interface AccuracyDetails {
  audio_url: string
  errors: AccuracyDetailsError[]
  id: number
  sentence?: string
  my_sentence?: string
  correct_sentence?: string
}

export interface AccuracyDetailsError {
  category: number
  title: string
  body: string
}

/**
 *
 */

export interface AccuracyMetricsDetailItem extends AccuracyDetailItem {
  page: PageItem
  details: AccuracyDetails[]
  errors: GrammarErrorDetailItem[]
}

export interface PageItem {
  prev: number | null
  curr: number
  next: number | null
}

export interface ComplexityDetailItem {
  count: number
  ratio: number
  level_tag: number
  level_type: string
  page: PageItem
  // 어휘 난이도
  words?: { [key: string]: number } | { [key: string]: { pos: string; count: number } }
  // 문장 종류
  sentence_tag?: number
  sentence_type?: string
  sentences?: string[]
}

interface ComplexityGraphDataDatumItem {
  ratio: number
  difficulty?: string
  type?: string
}

/**
 * CAF 2차 베타 추가 응답
 */

/**
 * caf/index 페이지 - summary overview
 */
export interface Count {
  twenty: number
  forty: number
}

export interface Average {
  overall: number
  complexity: number
  accuracy: number
  fluency: number
}

export interface TrendItem {
  start_time: TDateISO // 유저의 timezone에 맞게
  level: number
}

export type Trend = {
  ['overall']?: TrendItem[]
  ['complexity']: TrendItem[]
  ['accuracy']: TrendItem[]
  ['fluency']: TrendItem[]
  ['pronunciation']?: TrendItem[]
}

export type CafSummaryOverview = {
  count: Count
  average: Average
  trend: Trend
  show_summary_popup: boolean
  summary_popup_type: number
}

/**
 * ISO 8601 date format
 */
type TYear = `${number}${number}${number}${number}`
type TMonth = `${number}${number}`
type TDay = `${number}${number}`
type THours = `${number}${number}`
type TMinutes = `${number}${number}`
type TSeconds = `${number}${number}`

/**
 * string like `2021-01-08`
 */
export type TDateISODate = `${TYear}-${TMonth}-${TDay}`

/**
 * string like `14:42:34`
 */
type TDateISOTime = `${THours}:${TMinutes}:${TSeconds}`

export type TDateISO = `${TDateISODate}T${TDateISOTime}`

/**
 * caf/index 페이지 - summary highlights
 */

export type SummaryHighlightPinned = {
  tag: number
  count: number
  zscore_sum: number | null //  음수/양수로 weakness/strength 구분. 총합이 클수록 추천 순서 높음
}
// recommended highlights
export type SummaryHighlightRecommended = {
  tag: number
  average_count: number
  counts: CountData[]
}

export interface SummaryHighlightItem extends SummaryHighlightPinned, SummaryHighlightRecommended {
  is_pinned: boolean
}
export type SummaryHighlightResponse = SummaryHighlightItem[]

// 기간별
export type SummaryHighlightPerMetricItem = {
  tag: number
  count: number
  metric: string
  active: boolean
  zscore_sum: number
  average_count: number
}

export type CountData = {
  start_time: string
  count: number
}

/**
 * caf/index 페이지 - summary highlights per tag
 */
export type SummaryHighlightPerTagItem = {
  id: number
  highlight_tag: number
  start_time: string
  count: number
  details: string
}

export interface SummaryHighlightTag extends PageItem {
  component: string
  metric: string
  highlight_tag: number
  is_pinned: boolean
  highlights: SummaryHighlightItem[]
  count: number
}

export type HighlightTagGraphDataType = {
  component: string
  metric: string
  highlight_tag: number
  is_pinned: boolean
  highlights: SummaryHighlightItem[]
  count: number
}

export type SummaryHighlightTagResponse = SummaryHighlightTag

export interface SummaryLessonsResponse extends PageItem {
  lessons: SummaryLessonItem[]
  count: number
}

export type SummaryLessonItem = {
  lesson_id: number
  caf_setting_id: number
  allow_summary: boolean
  start_time: string
  formatted_start_time: string
  tutor_first_name: string
  material: string
  duration: number
  formatted_only_start_time: string
  formatted_only_date: string
  formatted_start_date: string
  levels: {
    // overall: number  // CAF Level은 3차 베타에서 삭제, 추후 다시 도입될 예정
    complexity: number
    accuracy: number
    fluency: number
    pronunciation?: number
  }
}

export interface SummaryDatesResponse {
  start_date: string
  caf_dates: string[]
}

export interface AIParaphraseCorrection {
  correction_id: number
  feedback: 0 | 1 | 2 // 0: 미선택, 1: 좋아요, 2: 싫어요
  response: string
  success: boolean
}
export interface AIParaphraseResponse {
  correction: AIParaphraseCorrection
  corrections_count: number
}

interface VocabScore {
  advanced: number
  high: number
  inter: number
  low: number
  beginner: number
}

interface SentenceScore {
  simple: number
  compound: number
  complex: number
  cc: number
  ohter: number
}

interface OtherScore {
  value: number | any
}

export interface ScoresResponse {
  top_25_ratio: VocabScore | SentenceScore | OtherScore | any
  native_ratio: VocabScore | SentenceScore | OtherScore | any
}

export interface CafShowTooltipResponse {
  value: string
}

export interface showJobBannerResponse {
  value: string
}
